// §13.3 SFX recipes on the §13.2 kit. Every id of §13.3 has a recipe here, plus the few extra
// cues §11 names (sub boom, cardboard thump, porcelain creak, rubbery pop, tape stop, siren, …).
// Recipes schedule nodes from t = 0 into an OfflineAudioContext; the pre-renderer renders three
// seeded variants of each. Each recipe is also exposed in the spec's `(ctx, variant) => void` form.
import { createKit, staircaseCurve, type Kit } from './kit';

export interface Recipe {
  /** Rendered length, seconds. */
  dur: number;
  render(k: Kit): void;
}

const cents = (f: number, c: number): number => f * (2 ** (c / 1200) - 1);

// ---- Building blocks ---------------------------------------------------------------------------

function bonkBody(k: Kit, peak: number): void {
  const f0 = 520 * k.jitter(0.03);
  const o = k.osc('sine', f0);
  k.pitchEnv(o.frequency, f0, 140 * k.jitter(0.03), 0.12);
  k.toOut(o, k.env({ a: 0.002, d: 0.17, s: 0, r: 0, peak }));
  // A soft octave partial gives the rubbery "bean" colour.
  const o2 = k.osc('triangle', f0 * 2);
  k.pitchEnv(o2.frequency, f0 * 2, 280, 0.08);
  k.toOut(o2, k.env({ a: 0.001, d: 0.06, s: 0, r: 0, peak: peak * 0.18 }));
}

function bonkClick(k: Kit, peak: number): void {
  k.toOut(k.noise('white', 0, 0.04), k.bp(2000 * k.jitter(0.05), 1), k.env({ a: 0.0005, d: 0.015, s: 0, r: 0, peak: peak * 1.4 }));
  const sub = k.osc('sine', 70);
  k.toOut(sub, k.env({ a: 0.003, d: 0.08, s: 0, r: 0, peak: peak * 0.9 }));
}

function whooshCore(k: Kit, from: number, to: number, dur: number, peak: number, q = 1.2, at = 0): void {
  const n = k.noise('white', at, at + dur + 0.02);
  const f = k.bp(from, q);
  k.pitchEnv(f.frequency, from, to, dur, 'exp', at);
  k.toOut(n, f, k.env({ a: dur * 0.38, d: dur * 0.62, s: 0, r: 0, peak, at }));
  // Pink body under the hiss.
  const p = k.noise('pink', at, at + dur + 0.02);
  const lp = k.lp(from * 1.5, 0.7);
  k.pitchEnv(lp.frequency, from * 1.5, to * 0.8, dur, 'exp', at);
  k.toOut(p, lp, k.env({ a: dur * 0.4, d: dur * 0.6, s: 0, r: 0, peak: peak * 0.45, at }));
}

function boing(k: Kit, freq: number, dur: number, peak: number, at = 0, rateFrom = 12, rateTo = 2, depth = 0.25): void {
  const o = k.osc('sine', freq, at);
  const lfo = k.osc('sine', rateFrom, at);
  k.pitchEnv(lfo.frequency, rateFrom, rateTo, dur, 'exp', at);
  const dg = k.gain(0);
  dg.gain.setValueAtTime(freq * depth, at);
  dg.gain.exponentialRampToValueAtTime(freq * 0.01, at + dur);
  lfo.connect(dg).connect(o.frequency);
  k.toOut(o, k.env({ a: 0.003, d: dur, s: 0, r: 0, peak, at }));
  const t = k.osc('triangle', freq * 2, at);
  dg.connect(t.frequency);
  k.toOut(t, k.env({ a: 0.002, d: dur * 0.5, s: 0, r: 0, peak: peak * 0.15, at }));
}

function blip(k: Kit, type: OscillatorType, freq: number, at: number, dur: number, peak: number): void {
  const o = k.osc(type, freq, at, at + dur + 0.01);
  k.toOut(o, k.env({ a: 0.002, d: dur, s: 0, r: 0, peak, at }));
}

/** An FM bell ping: inharmonic modulator, index decaying to 0. */
function ping(k: Kit, freq: number, at: number, dur: number, peak: number, ratio = 1.41, index = 3): void {
  const c = k.fm(freq, ratio, index, dur, at);
  k.toOut(c, k.env({ a: 0.001, d: dur, s: 0, r: 0, peak, at }));
}

function noiseBurst(k: Kit, color: 'white' | 'pink' | 'brown', filter: BiquadFilterNode, at: number, a: number, d: number, peak: number): void {
  k.toOut(k.noise(color, at, at + a + d + 0.01), filter, k.env({ a, d, s: 0, r: 0, peak, at }));
}

function explosionCore(k: Kit, scale: number): void {
  // Body: noise through a falling low-pass, gently saturated.
  const n = k.noise('pink');
  const lp = k.lp(3200 * scale, 0.9);
  k.pitchEnv(lp.frequency, 3200 * scale, 160, 0.65 * scale);
  k.toOut(n, lp, k.distort(0.18), k.env({ a: 0.003, d: 0.25, s: 0.35, r: 0.7 * scale, peak: 0.85 }));
  // Rumble and the sub drop carry the weight.
  const brown = k.noise('brown');
  k.toOut(brown, k.lp(260), k.env({ a: 0.005, d: 0.45, s: 0.35, r: 0.9 * scale, peak: 1 }));
  const sub = k.osc('sine', 90);
  k.pitchEnv(sub.frequency, 90, 30, 0.5 * scale);
  k.toOut(sub, k.env({ a: 0.004, d: 0.6 * scale, s: 0, r: 0, peak: 1 }));
  const sub2 = k.osc('sine', 120);
  k.pitchEnv(sub2.frequency, 120, 48, 0.4 * scale);
  k.toOut(sub2, k.env({ a: 0.004, d: 0.4 * scale, s: 0, r: 0, peak: 0.3 }));
  // Debris crackle, low in the mix.
  const cr = k.crackle(70, 1.2 * scale, 0.04);
  k.toOut(cr, k.bp(1700, 1.4), k.lp(3000), k.env({ a: 0.03, d: 0.3, s: 0.4, r: 0.6 * scale, peak: 0.22, at: 0.04 }));
  // A tight transient so it reads instantly.
  noiseBurst(k, 'white', k.lp(5000), 0, 0.0008, 0.025, 0.7);
}

function splashCore(k: Kit, peak: number): void {
  noiseBurst(k, 'white', k.lp(3200, 0.6), 0, 0.002, 0.06, peak);
}

function bubbles(k: Kit, count: number, at: number, span: number, lo: number, hi: number, peak: number): void {
  for (let i = 0; i < count; i++) {
    const t = at + k.rand() * span;
    const f = k.range(lo, hi);
    const o = k.osc('sine', f, t, t + 0.07);
    k.pitchEnv(o.frequency, f, f * k.range(1.8, 2.6), 0.05, 'exp', t);
    k.toOut(o, k.env({ a: 0.003, d: 0.05, s: 0, r: 0, peak: peak * k.range(0.5, 1), at: t }));
  }
}

function formantVoice(k: Kit, source: AudioNode, f1: number, f2: number, f3: number, q = 8): AudioNode {
  const sum = k.gain(1);
  const b1 = k.bp(f1, q);
  const b2 = k.bp(f2, q * 1.2);
  const b3 = k.bp(f3, q * 1.4);
  const g2 = k.gain(0.55);
  const g3 = k.gain(0.3);
  source.connect(b1).connect(sum);
  source.connect(b2).connect(g2).connect(sum);
  source.connect(b3).connect(g3).connect(sum);
  return sum;
}

function sawStack(k: Kit, freqs: readonly number[], at: number, detuneCents: number): GainNode {
  const mix = k.gain(1 / freqs.length);
  freqs.forEach((f, i) => {
    const o = k.osc('sawtooth', f, at);
    o.detune.value = (i % 2 === 0 ? 1 : -1) * detuneCents;
    o.connect(mix);
  });
  return mix;
}

// ---- Tonal cues in both modes ------------------------------------------------------------------
// Arpeggios and runs keyed to the song's root: a minor twin for minor and dorian keys, so a crate
// or crown never plays a major 3rd against a minor song.

const MINOR_THIRD = 2 ** (3 / 12);
const MAJOR_THIRD = 2 ** (4 / 12);

function cratePop(minor: boolean): Recipe {
  const third = minor ? MINOR_THIRD : MAJOR_THIRD;
  return {
    dur: 0.5, render: (k) => {
      const p = k.pluck(440, 0.45, 0.75);
      k.toOut(p, k.gain(0.8));
      noiseBurst(k, 'white', k.bp(1800, 0.8), 0, 0.001, 0.03, 0.35);
      // §13.3: sparkle blips on the triad one octave up, 40 ms apart.
      [1, third, 1.5, 2].forEach((r, i) => blip(k, 'sine', 880 * r, 0.04 + i * 0.04, 0.12, 0.28));
    },
  };
}

function sparkleRun(minor: boolean): Recipe {
  // C7 pentatonic (major or minor), keyed to the root at playback.
  const notes = minor ? [2093, 2489, 2794, 3136, 3729, 4186] : [2093, 2349, 2637, 3136, 3520, 4186];
  return {
    dur: 0.6, render: (k) => {
      for (let i = 0; i < 8; i++) blip(k, 'sine', k.pick(notes), i * 0.05 + k.rand() * 0.02, 0.12, 0.25);
    },
  };
}

function crownPop(minor: boolean): Recipe {
  const third = minor ? 2489 : 2637;
  return {
    dur: 0.5, render: (k) => {
      ping(k, third, 0, 0.3, 0.4, 2, 1.5);
      [2093, third, 3136, 4186].forEach((f, i) => blip(k, 'sine', f, 0.03 + i * 0.035, 0.1, 0.18));
    },
  };
}

function crownPick(minor: boolean): Recipe {
  const third = minor ? 622.25 : 659.25;
  return {
    dur: 0.45, render: (k) => {
      [523.25, third, 783.99].forEach((f, i) => {
        ping(k, f, i * 0.06, 0.3, 0.45, 2, 1.2);
        blip(k, 'triangle', f * 2, i * 0.06, 0.12, 0.12);
      });
    },
  };
}

function crownLost(minor: boolean): Recipe {
  return {
    dur: 0.4, render: (k) => {
      ping(k, minor ? 622.25 : 659.25, 0, 0.18, 0.4, 2, 1.2);
      ping(k, 523.25, 0.12, 0.25, 0.4, 2, 1.2);
    },
  };
}

// ---- Recipes -----------------------------------------------------------------------------------

export const RECIPES = {
  // Combat and movement
  bonk: { dur: 0.26, render: (k) => { bonkBody(k, 0.85); bonkClick(k, 0.7); } },
  bonkClick: {
    dur: 0.12, render: (k) => {
      bonkClick(k, 0.85);
      const o = k.osc('sine', 950);
      k.pitchEnv(o.frequency, 950, 380, 0.03);
      k.toOut(o, k.env({ a: 0.0008, d: 0.035, s: 0, r: 0, peak: 0.5 }));
    },
  },
  subBoom: {
    dur: 0.45, render: (k) => {
      const o = k.osc('sine', 72);
      k.pitchEnv(o.frequency, 72, 38, 0.35);
      // Envelope before the shaper: the shaper's oversampling latency must not outrun the attack.
      k.toOut(o, k.env({ a: 0.006, d: 0.4, s: 0, r: 0, peak: 1 }), k.distort(0.15));
    },
  },
  pow: {
    dur: 0.38, render: (k) => {
      bonkBody(k, 1);
      bonkClick(k, 0.9);
      const saw = k.osc('sawtooth', 120);
      k.pitchEnv(saw.frequency, 120, 70, 0.16);
      k.toOut(saw, k.lp(400, 1.6), k.env({ a: 0.002, d: 0.2, s: 0, r: 0, peak: 0.9 }));
      // The glove's leather slap: a short bright crack, not a hiss.
      noiseBurst(k, 'white', k.bp(4200, 0.8), 0, 0.001, 0.09, 0.22);
    },
  },
  clang: {
    dur: 1.25, render: (k) => {
      const base = 620 * k.jitter(0.004);
      const partials: readonly (readonly [number, number, number])[] = [[1, 0.5, 1.05], [2.76, 0.28, 0.7], [5.4, 0.16, 0.4]];
      for (const [r, amp, dec] of partials) {
        const c = k.fm(base * r, 1.41, 4, 0.6);
        k.toOut(c, k.lp(9000, 0.7), k.env({ a: 0.001, d: dec, s: 0, r: 0, peak: amp }));
      }
      noiseBurst(k, 'white', k.bp(3500, 0.9), 0, 0.0005, 0.03, 0.45);
    },
  },
  whoosh: { dur: 0.24, render: (k) => whooshCore(k, 400 * k.jitter(0.05), 2400 * k.jitter(0.05), 0.18, 0.8) },
  yoink: {
    dur: 0.22, render: (k) => {
      const o = k.osc('sawtooth', 200 * k.jitter(0.03));
      k.pitchEnv(o.frequency, 190, 260, 0.15);
      const f = k.lp(300, 12);
      k.pitchEnv(f.frequency, 300, 3000, 0.15);
      k.toOut(o, f, k.env({ a: 0.004, d: 0.17, s: 0, r: 0, peak: 0.55 }));
    },
  },
  whiff: { dur: 0.16, render: (k) => whooshCore(k, 500, 1800, 0.12, 0.48, 1.0) },
  yeet: {
    dur: 0.36, render: (k) => {
      whooshCore(k, 300, 3000, 0.3, 0.85, 1.4);
      const o = k.osc('sine', 500);
      k.pitchEnv(o.frequency, 500, 1300, 0.25);
      k.toOut(o, k.env({ a: 0.05, d: 0.2, s: 0, r: 0, peak: 0.08 }));
    },
  },
  jump: {
    dur: 0.12, render: (k) => {
      const o = k.osc('sine', 300 * k.jitter(0.03));
      k.pitchEnv(o.frequency, 300 * k.jitter(0.03), 600, 0.08);
      k.toOut(o, k.env({ a: 0.003, d: 0.09, s: 0, r: 0, peak: 0.6 }));
      const t = k.osc('triangle', 600);
      k.pitchEnv(t.frequency, 600, 1200, 0.08);
      k.toOut(t, k.env({ a: 0.002, d: 0.05, s: 0, r: 0, peak: 0.12 }));
    },
  },
  land: {
    dur: 0.12, render: (k) => {
      noiseBurst(k, 'brown', k.lp(120), 0, 0.002, 0.07, 1);
      const o = k.osc('sine', 85);
      k.pitchEnv(o.frequency, 85, 50, 0.06);
      k.toOut(o, k.env({ a: 0.002, d: 0.07, s: 0, r: 0, peak: 0.6 }));
      noiseBurst(k, 'white', k.bp(900, 0.8), 0, 0.001, 0.02, 0.12);
    },
  },
  step: { dur: 0.04, render: (k) => noiseBurst(k, 'white', k.bp(1500 * k.jitter(0.08), 1.2), 0, 0.001, 0.02, 0.5) },
  bigStep: {
    dur: 0.22, render: (k) => {
      const o = k.osc('sine', 55);
      k.pitchEnv(o.frequency, 62, 45, 0.12);
      k.toOut(o, k.env({ a: 0.003, d: 0.15, s: 0, r: 0, peak: 1 }));
      noiseBurst(k, 'brown', k.lp(220), 0, 0.002, 0.12, 0.8);
    },
  },
  teeterWobble: {
    dur: 1.3, render: (k) => {
      const o = k.osc('sine', 600);
      k.lfo(7, cents(600, 60), o.frequency);
      const trem = k.gain(0.75);
      k.lfo(7, 0.2, trem.gain);
      k.toOut(o, trem, k.gain(0.5));
      const breath = k.noise('pink');
      k.toOut(breath, k.bp(600, 4), k.gain(0.06));
    },
  },

  // Fall screams (cosmetic)
  slideWhistle: {
    dur: 1.1, render: (k) => {
      const o = k.osc('sine', 1600);
      k.pitchEnv(o.frequency, 1600, 300, 0.9);
      k.lfo(6, 12, o.frequency);
      k.toOut(o, k.env({ a: 0.03, d: 0.05, s: 0.85, hold: 0.75, r: 0.15, peak: 0.6 }));
      const air = k.noise('white');
      const bp = k.bp(1600, 6);
      k.pitchEnv(bp.frequency, 1600, 300, 0.9);
      k.toOut(air, bp, k.env({ a: 0.03, d: 0.05, s: 0.8, hold: 0.75, r: 0.15, peak: 0.08 }));
    },
  },
  opera: {
    dur: 1.35, render: (k) => {
      const src = k.osc('sawtooth', 520);
      k.pitchEnv(src.frequency, 520, 520 * 2 ** (-7 / 12), 1.0, 'exp', 0.2);
      const vibDepth = k.gain(0);
      const vibOsc = k.osc('sine', 5.5);
      vibOsc.connect(vibDepth).connect(src.frequency);
      vibDepth.gain.setValueAtTime(0, 0);
      vibDepth.gain.linearRampToValueAtTime(cents(520, 45), 0.35);
      const v = formantVoice(k, src, 800, 1150, 2900, 9);
      k.toOut(v, k.env({ a: 0.06, d: 0.1, s: 0.85, hold: 0.95, r: 0.22, peak: 0.9 }));
    },
  },
  yodel: {
    dur: 1.35, render: (k) => {
      const src = k.osc('sawtooth', 330);
      const b1 = k.bp(450, 8);
      const b2 = k.bp(800, 9);
      const lo = 330;
      const hi = 330 * 2 ** (9 / 12);
      for (let i = 0; i < 10; i++) {
        const t = i * 0.125;
        const up = i % 2 === 1;
        const drop = 1 - i * 0.025;
        src.frequency.setTargetAtTime((up ? hi : lo) * drop, t, 0.012);
        b1.frequency.setTargetAtTime(up ? 280 : 450, t, 0.02);
        b2.frequency.setTargetAtTime(up ? 2250 : 800, t, 0.02);
      }
      const sum = k.gain(1);
      src.connect(b1).connect(sum);
      const g2 = k.gain(0.6);
      src.connect(b2).connect(g2).connect(sum);
      k.toOut(sum, k.env({ a: 0.03, d: 0.05, s: 0.9, hold: 1.05, r: 0.15, peak: 1 }));
    },
  },
  dopplerNooo: {
    dur: 1.35, render: (k) => {
      const src = k.osc('sawtooth', 400);
      k.pitchEnv(src.frequency, 420, 200, 1.15);
      k.lfo(5, 6, src.frequency);
      const v = formantVoice(k, src, 325, 700, 2400, 8);
      const lp = k.lp(6000, 0.8);
      k.pitchEnv(lp.frequency, 6000, 600, 1.15);
      k.toOut(v, lp, k.env({ a: 0.05, d: 0.1, s: 0.9, hold: 0.95, r: 0.2, peak: 1 }));
    },
  },

  // Splash on KO (one per liquid)
  splooshTea: {
    dur: 0.75, render: (k) => {
      splashCore(k, 0.6);
      const n = k.noise('white');
      const lp = k.lp(1200, 1.5);
      k.pitchEnv(lp.frequency, 1200, 300, 0.3);
      k.toOut(n, lp, k.env({ a: 0.004, d: 0.32, s: 0, r: 0, peak: 1 }));
      const plop = k.osc('sine', 180);
      k.pitchEnv(plop.frequency, 260, 110, 0.12);
      k.toOut(plop, k.env({ a: 0.003, d: 0.14, s: 0, r: 0, peak: 0.5 }));
      bubbles(k, 7, 0.2, 0.45, 280, 620, 0.35);
    },
  },
  splooshMilk: {
    dur: 0.75, render: (k) => {
      splashCore(k, 0.35);
      const n = k.noise('pink');
      const lp = k.lp(900, 0.5);
      k.pitchEnv(lp.frequency, 900, 250, 0.35);
      k.toOut(n, lp, k.env({ a: 0.008, d: 0.36, s: 0, r: 0, peak: 1 }));
      const plop = k.osc('sine', 150);
      k.pitchEnv(plop.frequency, 210, 90, 0.16);
      k.toOut(plop, k.env({ a: 0.004, d: 0.18, s: 0, r: 0, peak: 0.6 }));
      bubbles(k, 3, 0.25, 0.35, 220, 420, 0.25);
    },
  },
  splooshFoam: {
    dur: 0.7, render: (k) => {
      // A soft plunge into suds: a watery body, an airy fizz and popping bubbles.
      splashCore(k, 0.35);
      const body = k.noise('pink');
      const lp = k.lp(1400, 0.8);
      k.pitchEnv(lp.frequency, 1400, 420, 0.32);
      k.toOut(body, lp, k.env({ a: 0.006, d: 0.34, s: 0, r: 0, peak: 0.9 }));
      const fizz = k.noise('white');
      k.toOut(fizz, k.hp(1800), k.lp(7500), k.env({ a: 0.02, d: 0.5, s: 0, r: 0, peak: 0.3 }));
      for (let i = 0; i < 9; i++) {
        const t = 0.06 + k.rand() * 0.5;
        noiseBurst(k, 'white', k.bp(k.range(1800, 4200), 5), t, 0.001, 0.01, k.range(0.15, 0.35));
      }
      bubbles(k, 7, 0.08, 0.5, 700, 1600, 0.3);
    },
  },
  splooshGrid: {
    dur: 0.55, render: (k) => {
      const o = k.osc('sawtooth', 1200);
      k.pitchEnv(o.frequency, 1200, 80, 0.4);
      const crush = k.ctx.createWaveShaper();
      crush.curve = staircaseCurve(6);
      k.toOut(o, crush, k.lp(5000), k.env({ a: 0.002, d: 0.45, s: 0, r: 0, peak: 0.7 }));
      const buzz = k.osc('square', 60);
      k.lfo(37, 25, buzz.frequency);
      k.toOut(buzz, k.lp(900), k.env({ a: 0.002, d: 0.3, s: 0, r: 0, peak: 0.35 }));
      noiseBurst(k, 'white', k.hp(3000), 0, 0.001, 0.05, 0.6);
    },
  },
  splooshLilac: {
    dur: 0.7, render: (k) => {
      splashCore(k, 0.35);
      const n = k.noise('pink');
      const lp = k.lp(1500, 0.8);
      k.pitchEnv(lp.frequency, 1500, 350, 0.3);
      k.toOut(n, lp, k.env({ a: 0.005, d: 0.3, s: 0, r: 0, peak: 0.9 }));
      bubbles(k, 5, 0.15, 0.4, 500, 1100, 0.3);
      for (let i = 0; i < 4; i++) blip(k, 'sine', [1568, 1976, 2349, 3136][i], 0.12 + i * 0.05, 0.12, 0.12);
    },
  },

  // Items and crates
  cratePop: cratePop(false),
  cratePopMinor: cratePop(true),
  crateWhistle: {
    dur: 1.05, render: (k) => {
      const o = k.osc('sine', 1200);
      k.pitchEnv(o.frequency, 1200, 500, 1.0);
      k.lfo(5, 6, o.frequency);
      k.toOut(o, k.env({ a: 0.08, d: 0.1, s: 0.85, hold: 0.7, r: 0.15, peak: 0.45 }));
    },
  },
  crateThump: {
    dur: 0.26, render: (k) => {
      const o = k.osc('sine', 110);
      k.pitchEnv(o.frequency, 115, 70, 0.1);
      k.toOut(o, k.env({ a: 0.002, d: 0.14, s: 0, r: 0, peak: 0.8 }));
      noiseBurst(k, 'pink', k.bp(420, 2.5), 0, 0.001, 0.1, 0.9);
      noiseBurst(k, 'white', k.lp(2500), 0, 0.001, 0.02, 0.3);
    },
  },
  bombTick: { dur: 0.05, render: (k) => blip(k, 'square', 1000, 0, 0.025, 0.4) },
  explosion: { dur: 1.45, render: (k) => explosionCore(k, 1) },
  peelSplat: {
    dur: 0.13, render: (k) => {
      noiseBurst(k, 'white', k.lp(800), 0, 0.002, 0.06, 1);
      const o = k.osc('sine', 320);
      k.pitchEnv(o.frequency, 320, 120, 0.04);
      k.toOut(o, k.env({ a: 0.001, d: 0.05, s: 0, r: 0, peak: 0.45 }));
    },
  },
  slip: {
    dur: 0.45, render: (k) => {
      const o = k.osc('sine', 900);
      k.pitchEnv(o.frequency, 900, 1400, 0.28);
      k.lfo(14, 70, o.frequency);
      k.toOut(o, k.env({ a: 0.01, d: 0.1, s: 0.7, hold: 0.18, r: 0.1, peak: 0.45 }));
      const n = k.noise('pink');
      k.toOut(n, k.bp(1100, 1.5), k.env({ a: 0.02, d: 0.42, s: 0, r: 0, peak: 0.4 }));
    },
  },
  balloonStretch: {
    dur: 0.6, render: (k) => {
      const o = k.osc('sawtooth', 180);
      k.pitchEnv(o.frequency, 170, 270, 0.5);
      k.lfo(9, 8, o.frequency);
      const f = k.lp(400, 10);
      k.pitchEnv(f.frequency, 400, 2500, 0.5);
      k.toOut(o, f, k.env({ a: 0.02, d: 0.1, s: 0.8, hold: 0.33, r: 0.12, peak: 0.4 }));
    },
  },
  balloonPop: {
    dur: 0.12, render: (k) => {
      noiseBurst(k, 'white', k.hp(2000), 0, 0.0005, 0.04, 1);
      blip(k, 'square', 3000, 0, 0.004, 0.5);
      noiseBurst(k, 'pink', k.lp(600), 0, 0.001, 0.05, 0.4);
    },
  },
  plungerThwop: {
    dur: 0.2, render: (k) => {
      const o = k.osc('sine', 200);
      k.pitchEnv(o.frequency, 210, 120, 0.1);
      k.toOut(o, k.env({ a: 0.002, d: 0.14, s: 0, r: 0, peak: 0.9 }));
      noiseBurst(k, 'pink', k.bp(600, 1.5), 0, 0.001, 0.05, 0.5);
    },
  },
  suction: {
    dur: 0.45, render: (k) => {
      const n = k.noise('white');
      const f = k.bp(800, 5);
      k.lfo(12, 500, f.frequency);
      k.toOut(n, f, k.env({ a: 0.03, d: 0.1, s: 0.7, hold: 0.2, r: 0.1, peak: 0.6 }));
    },
  },
  yank: { dur: 0.3, render: (k) => whooshCore(k, 400, 2600, 0.25, 0.75, 1.4) },
  shakeGlug: {
    dur: 0.9, render: (k) => {
      for (let i = 0; i < 3; i++) {
        const t = i * 0.14;
        const o = k.osc('sine', 180, t, t + 0.12);
        k.pitchEnv(o.frequency, 200, 150, 0.09, 'exp', t);
        k.toOut(o, k.lp(500), k.env({ a: 0.01, d: 0.09, s: 0, r: 0, peak: 0.9, at: t }));
        bubbles(k, 2, t + 0.02, 0.06, 300, 500, 0.15);
      }
      const b = k.osc('sine', 150, 0.45);
      k.pitchEnv(b.frequency, 150, 450, 0.25, 'exp', 0.45);
      k.toOut(b, k.env({ a: 0.01, d: 0.1, s: 0.75, hold: 0.15, r: 0.15, peak: 1, at: 0.45 }));
      const bt = k.osc('triangle', 300, 0.45);
      k.pitchEnv(bt.frequency, 300, 900, 0.25, 'exp', 0.45);
      k.toOut(bt, k.env({ a: 0.01, d: 0.2, s: 0, r: 0, peak: 0.12, at: 0.45 }));
    },
  },
  bwoopDown: {
    dur: 0.4, render: (k) => {
      const b = k.osc('sine', 450);
      k.pitchEnv(b.frequency, 450, 150, 0.3);
      k.toOut(b, k.env({ a: 0.01, d: 0.1, s: 0.6, hold: 0.12, r: 0.15, peak: 0.9 }));
    },
  },
  gloveWindup: { dur: 0.5, render: (k) => boing(k, 300, 0.48, 0.7) },
  bumperDing: {
    dur: 0.5, render: (k) => {
      // §13.3: FM bell, ratio 3.5, index 3 (decaying fast to a round sine body).
      const c = k.fm(1568, 3.5, 3, 0.08);
      k.toOut(c, k.lp(7000, 0.7), k.env({ a: 0.001, d: 0.45, s: 0, r: 0, peak: 0.6 }));
      const body = k.osc('sine', 1568);
      k.toOut(body, k.env({ a: 0.001, d: 0.42, s: 0, r: 0, peak: 0.35 }));
      const oct = k.osc('sine', 784);
      k.toOut(oct, k.env({ a: 0.002, d: 0.25, s: 0, r: 0, peak: 0.12 }));
    },
  },
  flipperClack: {
    dur: 0.12, render: (k) => {
      noiseBurst(k, 'white', k.hp(3000), 0, 0.0005, 0.015, 0.9);
      const o = k.osc('sine', 80);
      k.toOut(o, k.env({ a: 0.001, d: 0.04, s: 0, r: 0, peak: 0.9 }));
      // Solenoid thunk.
      noiseBurst(k, 'pink', k.bp(260, 2), 0.004, 0.001, 0.05, 0.6);
    },
  },
  pinballRumble: {
    dur: 2.4, render: (k) => {
      const n = k.noise('brown');
      const am = k.gain(0.75);
      k.lfo(8, 0.25, am.gain);
      k.toOut(n, k.lp(200, 0.9), am, k.gain(1.4));
    },
  },
  pinballLaunch: {
    dur: 0.6, render: (k) => {
      boing(k, 220, 0.35, 0.5, 0, 16, 4, 0.3);
      whooshCore(k, 500, 3000, 0.3, 0.6, 1.2, 0.08);
    },
  },
  tileCrack: {
    dur: 0.2, render: (k) => {
      for (let i = 0; i < 12; i++) noiseBurst(k, 'white', k.bp(k.range(2000, 4000), 3), k.rand() * 0.15, 0.0005, 0.005, k.range(0.4, 1));
    },
  },
  tileFall: {
    dur: 0.36, render: (k) => {
      const o = k.osc('sine', 90);
      k.pitchEnv(o.frequency, 100, 70, 0.2);
      k.toOut(o, k.env({ a: 0.004, d: 0.25, s: 0, r: 0, peak: 0.9 }));
      noiseBurst(k, 'pink', k.lp(500), 0, 0.006, 0.28, 0.9);
    },
  },
  regrowSparkle: sparkleRun(false),
  regrowSparkleMinor: sparkleRun(true),
  flameWhoosh: {
    dur: 0.7, render: (k) => {
      whooshCore(k, 250, 1400, 0.35, 0.8, 0.9);
      const n = k.noise('white');
      const am = k.gain(0);
      const cr = k.crackle(60, 0.65, 0.05);
      k.toOut(cr, k.lp(2000), k.env({ a: 0.05, d: 0.2, s: 0.5, hold: 0.1, r: 0.25, peak: 0.6 }));
      k.toOut(n, k.lp(2000), am);
      am.gain.setValueAtTime(0, 0);
      for (let t = 0.05; t < 0.6; t += 0.03) am.gain.linearRampToValueAtTime(k.rand() * 0.18 * (1 - t / 0.6), t);
      am.gain.linearRampToValueAtTime(0, 0.62);
    },
  },
  wedgeCrack: {
    dur: 0.32, render: (k) => {
      ping(k, 3000, 0, 0.18, 0.45, 1.73, 2);
      const cr = k.crackle(220, 0.25);
      k.toOut(cr, k.hp(1200), k.env({ a: 0.002, d: 0.25, s: 0, r: 0, peak: 0.9 }));
    },
  },
  ceramicClatter: {
    dur: 0.6, render: (k) => {
      for (let i = 0; i < 6; i++) ping(k, k.range(1800, 4200), k.rand() * 0.3, k.range(0.08, 0.2), k.range(0.25, 0.5), k.range(1.3, 2.4), 2.5);
      noiseBurst(k, 'white', k.hp(2500), 0, 0.001, 0.08, 0.3);
    },
  },
  sweeperHum: {
    dur: 2.4, render: (k) => {
      const o = k.osc('sawtooth', 55);
      const o2 = k.osc('sawtooth', 55);
      o2.detune.value = 9;
      const lp = k.lp(400, 2);
      k.lfo(1, 60, lp.frequency);
      const sum = k.gain(0.5);
      o.connect(sum);
      o2.connect(sum);
      k.toOut(sum, lp, k.gain(0.7));
    },
  },
  reverseBuzzer: {
    dur: 1.05, render: (k) => {
      const o = k.osc('square', 220);
      for (let i = 0; i < 4; i++) {
        o.frequency.setValueAtTime(220, i * 0.25);
        o.frequency.setValueAtTime(330, i * 0.25 + 0.125);
      }
      k.toOut(o, k.lp(2400), k.env({ a: 0.005, d: 0.02, s: 0.9, hold: 0.95, r: 0.06, peak: 0.35 }));
    },
  },
  sweeperClunk: {
    dur: 0.32, render: (k) => {
      noiseBurst(k, 'pink', k.bp(420, 2), 0, 0.001, 0.12, 1);
      const o = k.osc('sine', 70);
      k.toOut(o, k.env({ a: 0.002, d: 0.14, s: 0, r: 0, peak: 0.7 }));
      ping(k, 1250, 0.005, 0.22, 0.2, 2.1, 3);
    },
  },
  bubblePop: {
    dur: 0.08, render: (k) => {
      const o = k.osc('sine', 1200);
      k.pitchEnv(o.frequency, 1200, 2400, 0.03);
      k.toOut(o, k.env({ a: 0.001, d: 0.035, s: 0, r: 0, peak: 0.8 }));
      noiseBurst(k, 'white', k.hp(4000), 0, 0.0005, 0.006, 0.4);
    },
  },
  bubbleWobble: {
    dur: 0.6, render: (k) => {
      const o = k.osc('sine', 320);
      k.lfo(9, 60, o.frequency);
      const am = k.gain(0.6);
      k.lfo(9, 0.35, am.gain);
      k.toOut(o, am, k.env({ a: 0.05, d: 0.1, s: 0.8, hold: 0.3, r: 0.12, peak: 0.6 }));
    },
  },
  cupWhirl: {
    dur: 2.4, render: (k) => {
      const n = k.noise('white');
      const f = k.bp(900, 3);
      k.lfo(3, 400, f.frequency);
      k.toOut(n, f, k.gain(0.6));
      const w = k.osc('sine', 700);
      k.lfo(3, 120, w.frequency);
      k.toOut(w, k.gain(0.04));
    },
  },
  fling: {
    dur: 0.5, render: (k) => {
      boing(k, 260, 0.4, 0.55, 0, 14, 3, 0.35);
      whooshCore(k, 600, 2800, 0.25, 0.55, 1.2, 0.03);
    },
  },
  stompCrunch: {
    dur: 0.42, render: (k) => {
      noiseBurst(k, 'white', k.bp(1500, 1.5), 0, 0.001, 0.06, 1);
      const cr = k.crackle(300, 0.08);
      k.toOut(cr, k.hp(1500), k.env({ a: 0.001, d: 0.08, s: 0, r: 0, peak: 0.7 }));
      const o = k.osc('sine', 200, 0.04);
      k.pitchEnv(o.frequency, 200, 400, 0.15, 'exp', 0.04);
      k.lfo(11, 30, o.frequency, 'sine', 0.04);
      k.toOut(o, k.env({ a: 0.005, d: 0.3, s: 0, r: 0, peak: 0.5, at: 0.04 }));
    },
  },
  escapePop: {
    dur: 0.16, render: (k) => {
      const o = k.osc('sine', 420);
      k.pitchEnv(o.frequency, 460, 150, 0.08);
      k.toOut(o, k.env({ a: 0.001, d: 0.1, s: 0, r: 0, peak: 0.9 }));
      noiseBurst(k, 'white', k.bp(2500, 1), 0, 0.0005, 0.01, 0.5);
    },
  },
  porcelainCreak: {
    dur: 0.62, render: (k) => {
      const o = k.osc('sawtooth', 85);
      k.lfo(23, 18, o.frequency, 'square');
      k.pitchEnv(o.frequency, 85, 70, 0.55);
      k.toOut(o, k.bp(1200, 6), k.env({ a: 0.06, d: 0.1, s: 0.7, hold: 0.25, r: 0.18, peak: 0.6 }));
      const cr = k.crackle(70, 0.55, 0.03);
      k.toOut(cr, k.hp(2500), k.gain(0.3));
    },
  },

  // Honks (cosmetic; also ghost honks)
  clownHorn: {
    dur: 0.42, render: (k) => {
      const src = k.osc('sawtooth', 440);
      k.pitchEnv(src.frequency, 440, 440 * 2 ** (2 / 12), 0.12, 'exp', 0.02);
      const v = formantVoice(k, src, 800, 1150, 2400, 6);
      const rub = k.distort(0.25);
      k.toOut(v, rub, k.env({ a: 0.012, d: 0.05, s: 0.85, hold: 0.22, r: 0.08, peak: 0.8 }));
    },
  },
  duck: {
    dur: 0.26, render: (k) => {
      const o = k.osc('square', 900);
      k.pitchEnv(o.frequency, 900, 700, 0.16);
      k.toOut(o, k.bp(1500, 3), k.env({ a: 0.004, d: 0.06, s: 0.6, hold: 0.08, r: 0.06, peak: 0.9 }));
      const o2 = k.osc('square', 450);
      k.pitchEnv(o2.frequency, 450, 350, 0.16);
      k.toOut(o2, k.bp(900, 4), k.env({ a: 0.004, d: 0.12, s: 0, r: 0, peak: 0.35 }));
    },
  },
  kazoo: {
    dur: 0.5, render: (k) => {
      const o = k.osc('square', 330);
      k.lfo(6, cents(330, 35), o.frequency);
      const buzz = k.distort(0.4);
      k.toOut(o, buzz, k.bp(1200, 2.5), k.env({ a: 0.02, d: 0.05, s: 0.85, hold: 0.32, r: 0.08, peak: 0.85 }));
    },
  },
  foghorn: {
    dur: 1, render: (k) => {
      const mix = sawStack(k, [70, 71], 0, 0);
      k.toOut(mix, k.lp(400, 1.2), k.env({ a: 0.08, d: 0.1, s: 0.9, hold: 0.6, r: 0.18, peak: 1 }));
    },
  },
  bikeBell: {
    dur: 0.8, render: (k) => {
      ping(k, 2400, 0, 0.5, 0.5, 1.41, 2.5);
      ping(k, 2400, 0.12, 0.6, 0.45, 1.41, 2.5);
      ping(k, 3360, 0, 0.3, 0.15, 1.41, 1.5);
    },
  },
  squeaky: {
    dur: 0.2, render: (k) => {
      const o = k.osc('sine', 1800);
      o.frequency.setValueAtTime(1800, 0);
      o.frequency.linearRampToValueAtTime(2600, 0.075);
      o.frequency.linearRampToValueAtTime(1800, 0.15);
      k.toOut(o, k.env({ a: 0.008, d: 0.02, s: 0.85, hold: 0.1, r: 0.04, peak: 0.6 }));
    },
  },
  goose: {
    dur: 0.36, render: (k) => {
      const o = k.osc('sawtooth', 700);
      k.pitchEnv(o.frequency, 740, 640, 0.25);
      k.toOut(o, k.distort(0.6), k.bp(2000, 2.2), k.env({ a: 0.006, d: 0.04, s: 0.8, hold: 0.18, r: 0.1, peak: 0.7 }));
    },
  },
  airhorn: {
    dur: 0.76, render: (k) => {
      const mix = k.gain(0.33);
      for (const f of [466, 554, 698]) {
        for (const d of [-8, 8]) {
          const o = k.osc('sawtooth', f);
          o.detune.value = d;
          o.connect(mix);
        }
      }
      k.toOut(mix, k.distort(0.25), k.lp(4500), k.env({ a: 0.01, d: 0.03, s: 0.9, hold: 0.6, r: 0.08, peak: 0.6 }));
    },
  },

  // Ghost pebbles, respawn, crown, potato
  pebbleDrop: {
    dur: 0.45, render: (k) => {
      const o = k.osc('sine', 1500);
      k.pitchEnv(o.frequency, 1500, 700, 0.4);
      k.toOut(o, k.env({ a: 0.03, d: 0.1, s: 0.6, hold: 0.2, r: 0.1, peak: 0.35 }));
    },
  },
  pebbleTok: {
    dur: 0.08, render: (k) => {
      const o = k.osc('sine', 900);
      k.toOut(o, k.env({ a: 0.0008, d: 0.02, s: 0, r: 0, peak: 0.9 }));
      noiseBurst(k, 'white', k.bp(2500, 2), 0, 0.0005, 0.01, 0.4);
    },
  },
  parachute: {
    dur: 0.8, render: (k) => {
      noiseBurst(k, 'white', k.bp(1500, 1), 0, 0.001, 0.03, 0.8);
      const n = k.noise('pink');
      const am = k.gain(0.5);
      k.lfo(18, 0.45, am.gain, 'triangle');
      k.toOut(n, k.bp(700, 1.5), am, k.env({ a: 0.03, d: 0.1, s: 0.7, hold: 0.4, r: 0.2, peak: 0.6, at: 0.03 }));
    },
  },
  crownPop: crownPop(false),
  crownPopMinor: crownPop(true),
  crownPick: crownPick(false),
  crownPickMinor: crownPick(true),
  crownTick: { dur: 0.06, render: (k) => ping(k, 1046.5, 0, 0.05, 0.4, 2, 0.8) },
  crownLost: crownLost(false),
  crownLostMinor: crownLost(true),
  potatoBeep: { dur: 0.06, render: (k) => blip(k, 'square', 1320, 0, 0.04, 0.35) },
  potatoBoom: { dur: 1.7, render: (k) => explosionCore(k, 1.3) },
  potatoPass: { dur: 0.25, render: (k) => whooshCore(k, 700, 2600, 0.16, 0.7, 1.4) },
  countdownBeep: {
    dur: 0.14, render: (k) => {
      const o = k.osc('sine', 659.25);
      k.toOut(o, k.env({ a: 0.003, d: 0.02, s: 0.8, hold: 0.07, r: 0.03, peak: 0.6 }));
      const o2 = k.osc('triangle', 1318.5);
      k.toOut(o2, k.env({ a: 0.002, d: 0.05, s: 0, r: 0, peak: 0.1 }));
    },
  },
  ghostWhoo: {
    dur: 1, render: (k) => {
      const o = k.osc('sine', 520);
      k.pitchEnv(o.frequency, 440, 700, 0.5);
      k.lfo(5, 18, o.frequency);
      k.toOut(o, k.env({ a: 0.15, d: 0.1, s: 0.8, hold: 0.45, r: 0.25, peak: 0.45 }));
      const air = k.noise('pink');
      k.toOut(air, k.bp(700, 3), k.env({ a: 0.2, d: 0.1, s: 0.7, hold: 0.4, r: 0.25, peak: 0.12 }));
    },
  },
  itemGet: {
    dur: 0.26, render: (k) => {
      blip(k, 'triangle', 1046.5, 0, 0.08, 0.4);
      blip(k, 'triangle', 1568, 0.06, 0.15, 0.4);
    },
  },
  vendClunk: {
    dur: 0.42, render: (k) => {
      noiseBurst(k, 'pink', k.bp(300, 2), 0, 0.001, 0.12, 0.9);
      ping(k, 1975, 0.12, 0.25, 0.35, 2.4, 2);
      ping(k, 2637, 0.18, 0.2, 0.25, 2.4, 2);
    },
  },
  headBoing: { dur: 0.36, render: (k) => boing(k, 250, 0.34, 0.6, 0, 10, 3, 0.3) },
  tapeStop: {
    dur: 0.42, render: (k) => {
      // A saw triad whose pitch ramps down an octave over 300 ms, closing a low-pass as it slows.
      const mix = k.gain(0.33);
      for (const f of [261.63, 329.63, 392]) {
        const o = k.osc('sawtooth', f);
        o.frequency.setValueAtTime(f, 0);
        o.frequency.setValueCurveAtTime(new Float32Array([f, f * 0.97, f * 0.9, f * 0.78, f * 0.62, f * 0.5]), 0.02, 0.3);
        o.connect(mix);
      }
      const lp = k.lp(3200, 0.8);
      k.pitchEnv(lp.frequency, 3200, 500, 0.32);
      k.toOut(mix, lp, k.env({ a: 0.004, d: 0.05, s: 0.9, hold: 0.22, r: 0.1, peak: 0.7 }));
    },
  },
  vhsRewind: {
    dur: 0.5, render: (k) => {
      const n = k.noise('white');
      const f = k.bp(3000, 5);
      k.lfo(23, 1500, f.frequency);
      k.toOut(n, f, k.env({ a: 0.02, d: 0.1, s: 0.7, hold: 0.25, r: 0.1, peak: 0.5 }));
      const o = k.osc('square', 1800);
      k.lfo(31, 600, o.frequency);
      k.toOut(o, k.lp(3000), k.env({ a: 0.02, d: 0.1, s: 0.5, hold: 0.25, r: 0.1, peak: 0.08 }));
    },
  },
  siren: {
    dur: 2.1, render: (k) => {
      const o = k.osc('sawtooth', 520);
      o.frequency.setValueAtTime(520, 0);
      o.frequency.linearRampToValueAtTime(980, 0.5);
      o.frequency.linearRampToValueAtTime(520, 1.0);
      o.frequency.linearRampToValueAtTime(980, 1.5);
      o.frequency.linearRampToValueAtTime(520, 2.0);
      k.toOut(o, k.lp(2200, 1), k.env({ a: 0.05, d: 0.1, s: 0.85, hold: 1.75, r: 0.15, peak: 0.45 }));
      const s = k.osc('sine', 520);
      s.frequency.setValueAtTime(520, 0);
      s.frequency.linearRampToValueAtTime(980, 0.5);
      s.frequency.linearRampToValueAtTime(520, 1.0);
      s.frequency.linearRampToValueAtTime(980, 1.5);
      s.frequency.linearRampToValueAtTime(520, 2.0);
      k.toOut(s, k.env({ a: 0.05, d: 0.1, s: 0.85, hold: 1.75, r: 0.15, peak: 0.35 }));
    },
  },

  // Telegraphs and extra §11 cues
  fuseLight: {
    dur: 0.5, render: (k) => {
      const n = k.noise('white');
      const bp = k.bp(5200, 1.5);
      k.lfo(31, 1800, bp.frequency);
      k.toOut(n, bp, k.env({ a: 0.01, d: 0.1, s: 0.6, hold: 0.22, r: 0.12, peak: 0.6 }));
      const cr = k.crackle(80, 0.45);
      k.toOut(cr, k.hp(3000), k.gain(0.35));
      blip(k, 'square', 1000, 0, 0.025, 0.25);
    },
  },
  candleFlicker: {
    dur: 1.1, render: (k) => {
      // Flicker growth over a bar: a breathy flame swelling with crackles.
      const n = k.noise('pink');
      const lp = k.lp(500, 0.8);
      k.pitchEnv(lp.frequency, 500, 2200, 1.0);
      const am = k.gain(0.7);
      k.lfo(9, 0.3, am.gain);
      k.toOut(n, lp, am, k.env({ a: 0.85, d: 0.05, s: 0.9, hold: 0.05, r: 0.12, peak: 0.55 }));
      const cr = k.crackle(45, 1.0, 0.05);
      k.toOut(cr, k.hp(1800), k.env({ a: 0.6, d: 0.1, s: 0.8, hold: 0.2, r: 0.1, peak: 0.45, at: 0.05 }));
    },
  },
  flipperBlink: {
    dur: 0.09, render: (k) => {
      blip(k, 'square', 1760, 0, 0.035, 0.22);
      blip(k, 'sine', 3520, 0, 0.02, 0.12);
    },
  },
  ballAlarm: {
    dur: 1.0, render: (k) => {
      for (let i = 0; i < 4; i++) {
        const t = i * 0.22;
        ping(k, i % 2 === 0 ? 1318.5 : 1760, t, 0.18, 0.4, 2, 1.5);
        blip(k, 'square', i % 2 === 0 ? 659.25 : 880, t, 0.08, 0.12);
      }
    },
  },
  armDrop: {
    dur: 0.6, render: (k) => {
      whooshCore(k, 2400, 300, 0.3, 0.5, 1.2);
      const o = k.osc('sine', 70, 0.28);
      k.pitchEnv(o.frequency, 90, 45, 0.18, 'exp', 0.28);
      k.toOut(o, k.env({ a: 0.003, d: 0.25, s: 0, r: 0, peak: 1, at: 0.28 }));
      noiseBurst(k, 'pink', k.bp(500, 1.5), 0.28, 0.001, 0.12, 0.7);
    },
  },

  // UI (§13.3)
  hover: { dur: 0.03, render: (k) => blip(k, 'sine', 1800, 0, 0.015, 0.4) },
  click: { dur: 0.3, render: (k) => k.toOut(k.pluck(660, 0.28, 0.6), k.gain(0.8)) },
  back: {
    dur: 0.34, render: (k) => {
      // §13.3: pluck 440 → 330 Hz (a soft, rounded pair: "back").
      k.toOut(k.pluck(440, 0.14, 0.35), k.lp(3000), k.env({ a: 0.001, d: 0.06, s: 0.5, hold: 0.02, r: 0.03, peak: 0.8 }));
      k.toOut(k.pluck(330, 0.25, 0.35, 0.07), k.lp(2600), k.gain(0.8));
      k.toOut(k.osc('sine', 440, 0, 0.1), k.env({ a: 0.002, d: 0.07, s: 0, r: 0, peak: 0.25 }));
      k.toOut(k.osc('sine', 330, 0.07, 0.3), k.env({ a: 0.002, d: 0.16, s: 0, r: 0, peak: 0.25, at: 0.07 }));
    },
  },
  error: {
    dur: 0.17, render: (k) => {
      const o = k.osc('square', 150);
      k.toOut(o, k.lp(1800), k.env({ a: 0.004, d: 0.02, s: 0.85, hold: 0.1, r: 0.03, peak: 0.45 }));
      const o2 = k.osc('square', 158);
      k.toOut(o2, k.lp(1800), k.env({ a: 0.004, d: 0.02, s: 0.85, hold: 0.1, r: 0.03, peak: 0.25 }));
    },
  },
  coin: {
    dur: 0.4, render: (k) => {
      ping(k, 1975.5, 0, 0.12, 0.35, 2, 1.2);
      ping(k, 2637, 0.06, 0.32, 0.4, 2, 1.2);
    },
  },
  drumroll: {
    dur: 1.6, render: (k) => {
      // A snare roll as one amplitude curve: 48 strokes with a crescendo, the last one accented.
      const hits = 48;
      const span = 1.5;
      const n = 1600;
      const curve = new Float32Array(n);
      const body = new Float32Array(n);
      for (let i = 0; i < hits; i++) {
        const t = i * (span / hits) + (k.rand() - 0.5) * 0.004;
        const amp = (0.18 + 0.7 * (i / hits) ** 1.6) * (0.85 + 0.3 * k.rand()) * (i === hits - 1 ? 1.25 : 1);
        for (let j = 0; j < n; j++) {
          const dt = (j / (n - 1)) * k.dur - t;
          if (dt < 0 || dt > 0.06) continue;
          const e = dt < 0.001 ? dt / 0.001 : Math.exp(-(dt - 0.001) / 0.012);
          curve[j] = Math.max(curve[j], amp * e);
          body[j] = Math.max(body[j], amp * 0.35 * (dt < 0.001 ? dt / 0.001 : Math.exp(-(dt - 0.001) / 0.01)));
        }
      }
      curve[0] = 0;
      curve[n - 1] = 0;
      body[0] = 0;
      body[n - 1] = 0;
      const snare = k.gain(0);
      snare.gain.setValueCurveAtTime(curve, 0, k.dur);
      k.toOut(k.noise('white'), k.bp(2200, 0.9), snare);
      const drum = k.gain(0);
      drum.gain.setValueCurveAtTime(body, 0, k.dur);
      k.toOut(k.osc('sine', 190), drum);
    },
  },
  cardFlip: {
    dur: 0.32, render: (k) => {
      whooshCore(k, 900, 3500, 0.12, 0.45, 1.2);
      k.toOut(k.pluck(880, 0.2, 0.7, 0.1), k.gain(0.6));
    },
  },
  toast: {
    dur: 0.25, render: (k) => {
      const o = k.osc('sine', 700);
      k.pitchEnv(o.frequency, 900, 520, 0.06);
      k.toOut(o, k.env({ a: 0.002, d: 0.1, s: 0, r: 0, peak: 0.55 }));
      noiseBurst(k, 'pink', k.bp(2200, 1.2), 0.01, 0.002, 0.06, 0.3);
    },
  },
  uiTick: { dur: 0.08, render: (k) => k.toOut(k.pluck(1320, 0.07, 0.8), k.gain(0.6)) },
} satisfies Record<string, Recipe>;

export type SfxId = keyof typeof RECIPES;
export const SFX_IDS: readonly SfxId[] = Object.keys(RECIPES) as SfxId[];

/** The spec's recipe form: schedules variant `variant` of `id` into `ctx`'s destination. */
export function recipeFn(id: SfxId): (ctx: OfflineAudioContext, variant: number) => void {
  return (ctx, variant) => {
    const dc = ctx.createBiquadFilter();
    dc.type = 'highpass';
    dc.frequency.value = 15;
    dc.connect(ctx.destination);
    RECIPES[id].render(createKit(ctx, variant, dc, RECIPES[id].dur, hashId(id)));
  };
}

export function hashId(id: string): number {
  let h = 7;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return h;
}
